import { useState } from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Plus, Sparkles, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GoalCard } from "./GoalCard";
import { GoalDialog } from "./GoalDialog";
import { QuickAdd } from "./QuickAdd";
import { CATEGORIES } from "@/lib/categories";

const FILTERS = [["all", "All"], ["todo", "Still to do"], ["done", "Done"]];

export const LifeList = ({ goals, b }) => {
  const [filter, setFilter] = useState("all");
  const [cat, setCat] = useState("all");
  const done = goals.filter((g) => g.done).length;
  const pct = goals.length ? Math.round((done / goals.length) * 100) : 0;
  const shown = goals.map((g, i) => ({ g, i }))
    .filter(({ g }) => filter === "all" || (filter === "done" ? g.done : !g.done))
    .filter(({ g }) => cat === "all" || (g.category || "") === cat);
  const used = CATEGORIES.filter((c) => goals.some((g) => g.category === c.key));
  const sortable = !b.readOnly && filter === "all" && cat === "all";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = goals.findIndex((g) => g.id === active.id);
    const to = goals.findIndex((g) => g.id === over.id);
    b.reorderGoals(arrayMove(goals, from, to));
  };
  const onMove = (i, d) => b.reorderGoals(arrayMove(goals, i, i + d));

  return (
    <section id="life" className="scroll-mt-24 px-4 sm:px-8 lg:px-12 py-20" data-testid="life-bucketlist-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
        <div>
          <p className="eyebrow !text-rose-300/80">Section 02</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Before I die</h2>
          <p className="text-slate-400 mt-3 max-w-md text-sm sm:text-base">The things that make a life feel lived. Tick them off one by one.</p>
          {sortable && goals.length > 1 && <p className="text-xs text-slate-500 mt-2" data-testid="life-reorder-hint">Drag the <GripVertical size={12} className="inline -mt-0.5" /> handle or use the arrows to reorder.</p>}
        </div>
        <div className="flex flex-col gap-4 lg:items-end">
          <div className="w-full sm:w-80">
            <div className="flex justify-between font-mono text-xs text-slate-400 mb-2">
              <span data-testid="life-progress-count">{done} / {goals.length} done</span><span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-400 to-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="life-progress-bar" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`life-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === k ? "bg-white text-[#0B0F17] border-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
            {!b.readOnly && <GoalDialog onSave={b.addGoal} testId="goal-add-dialog">
              <Button size="sm" className="rounded-full bg-rose-400 text-[#1f0a0e] hover:bg-rose-300 ml-2" data-testid="add-life-bucket-item-button"><Plus size={14} /> New goal</Button>
            </GoalDialog>}
          </div>
        </div>
      </div>
      {used.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="life-category-filters">
          <span className="eyebrow !text-slate-500 mr-1">Category</span>
          <button onClick={() => setCat("all")} data-testid="life-category-filter-all"
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${cat === "all" ? "bg-white text-[#0B0F17] border-white" : "border-white/10 text-slate-400 hover:text-white"}`}>All</button>
          {used.map((c) => {
            const on = cat === c.key;
            const count = goals.filter((g) => g.category === c.key).length;
            return (
              <button key={c.key} onClick={() => setCat(on ? "all" : c.key)} data-testid={`life-category-filter-${c.key}`}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-colors duration-200"
                style={on ? { background: c.color, borderColor: c.color, color: "#0B0F17" } : { borderColor: `${c.color}55`, color: c.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? "#0B0F17" : c.color }} />{c.label} <span className="font-mono opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}
      {shown.length === 0 && !b.readOnly && filter !== "done" && goals.length === 0 ? (
        <ol className="max-w-5xl border-y border-white/[0.06]"><QuickAdd b={b} /></ol>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center text-slate-500" data-testid="life-empty">
          <Sparkles className="mx-auto mb-3 opacity-60" /> Nothing here yet.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={shown.map(({ g }) => g.id)} strategy={verticalListSortingStrategy}>
            <ol className="max-w-5xl divide-y divide-white/[0.06] border-y border-white/[0.06]" data-testid="life-list">
              {shown.map(({ g, i }) => <GoalCard key={g.id} g={g} i={i} b={b} sortable={sortable} onMove={onMove} last={i === goals.length - 1} />)}
              {!b.readOnly && filter !== "done" && <QuickAdd b={b} />}
            </ol>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
};
